'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface Marker {
  id: string;
  x: number;
  y: number;
  label: string;
  description: string;
}

const SAMPLE_DATA = {
  title: '1959 Gibson Les Paul Standard',
  year: '1959',
  category: 'Vintage Guitar',
  description: 'An exceptionally rare 1959 Gibson Les Paul Standard in original sunburst finish. This instrument represents the pinnacle of electric guitar design, with its lightweight mahogany body, figured maple top, and PAF humbucking pickups. The guitar has been professionally maintained and retains all original components.',
  collectorNotes: 'Acquired from the estate of a session musician who played on numerous Motown recordings in the 1960s. The wear patterns tell the story of countless studio sessions and live performances.',
  signedBy: ['Eric Clapton', 'Jimmy Page'],
  images: [
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_19aa54d73-1765753346482.png", alt: 'Front view of 1959 Les Paul' },
  { url: "https://images.unsplash.com/photo-1702859963843-246255e6a65f", alt: 'Headstock detail' },
  { url: "https://images.unsplash.com/photo-1695506976351-d7aa68bb7d67", alt: 'Back view showing grain' }],

  authenticity: {
    authenticated: true,
    inPerson: true,
    signed: true,
    certified: true,
    certifiedBy: 'Gruhn Guitars',
    certificateNumber: 'GG-LP-1959-0847',
    certificationDate: 'March 15, 2023'
  },
  markers: [
  { id: 'm1', x: 45, y: 25, label: '🎸', description: 'Original PAF pickup - Patent Applied For humbucker' },
  { id: 'm2', x: 50, y: 15, label: '🔧', description: 'Kluson tuners - All original from 1959' },
  { id: 'm3', x: 48, y: 45, label: '✨', description: 'Flame maple top - AAA grade figuring' },
  { id: 'm4', x: 52, y: 60, label: '🎚️', description: 'Original potentiometers dated 1959' },
  { id: 'm5', x: 46, y: 75, label: '🔒', description: 'Trapeze tailpiece - Factory original' },
  { id: 'm6', x: 55, y: 35, label: '📝', description: 'Serial number stamped in cavity' }]

};

export default function ModernMinimalViewer() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showMarkers, setShowMarkers] = useState(false);
  const [hoveredMarker, setHoveredMarker] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Clean Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-blue-600 uppercase tracking-wider">
              {SAMPLE_DATA.category}
            </div>
            <div className="text-sm text-gray-500">{SAMPLE_DATA.year}</div>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mt-2">
            {SAMPLE_DATA.title}
          </h1>
        </div>
      </header>

      {/* Main Content - 40/60 Split */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left Column - Image Gallery (40%) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Main Image */}
            <div className="relative bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="aspect-[3/4] relative">
                <Image
                  src={SAMPLE_DATA.images[currentImageIndex].url}
                  alt={SAMPLE_DATA.images[currentImageIndex].alt}
                  fill
                  className="object-cover" />
                
                
                {/* Interactive Markers */}
                {showMarkers && SAMPLE_DATA.markers.map((marker) =>
                <div
                  key={marker.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-300 hover:scale-125 z-10"
                  style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
                  onMouseEnter={() => setHoveredMarker(marker.id)}
                  onMouseLeave={() => setHoveredMarker(null)}>
                  
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg border-2 border-white">
                      <span className="text-lg">{marker.label}</span>
                    </div>
                    
                    {/* Tooltip */}
                    {hoveredMarker === marker.id &&
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-900 text-white text-xs rounded-xl p-3 shadow-xl">
                        <div className="font-medium mb-1">{marker.label} Detail</div>
                        <div className="text-gray-300">{marker.description}</div>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                          <div className="w-2 h-2 bg-gray-900 rotate-45"></div>
                        </div>
                      </div>
                  }
                  </div>
                )}
              </div>
            </div>

            {/* Markers Toggle */}
            <button
              onClick={() => setShowMarkers(!showMarkers)}
              className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-full transition-all duration-300 shadow-md hover:shadow-lg">
              
              {showMarkers ? '✓ Markers Visible' : 'Show Interactive Markers'}
            </button>

            {/* Thumbnail Gallery */}
            <div className="flex gap-3">
              {SAMPLE_DATA.images.map((img, idx) =>
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`flex-1 aspect-square rounded-2xl overflow-hidden transition-all duration-300 ${
                currentImageIndex === idx ?
                'ring-4 ring-blue-600 ring-offset-2' : 'opacity-60 hover:opacity-100'}`
                }>
                
                  <Image
                  src={img.url}
                  alt={img.alt}
                  width={120}
                  height={120}
                  className="w-full h-full object-cover" />
                
                </button>
              )}
            </div>
          </div>

          {/* Right Column - Content (60%) */}
          <div className="lg:col-span-3 space-y-8">
            {/* Authenticity Badges */}
            <div className="flex flex-wrap gap-3">
              {SAMPLE_DATA.authenticity.authenticated &&
              <div className="px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium border border-green-200">
                  ✓ Authenticated
                </div>
              }
              {SAMPLE_DATA.authenticity.inPerson &&
              <div className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200">
                  ✓ In-Person
                </div>
              }
              {SAMPLE_DATA.authenticity.signed &&
              <div className="px-4 py-2 bg-purple-50 text-purple-700 rounded-full text-sm font-medium border border-purple-200">
                  ✓ Signed
                </div>
              }
              {SAMPLE_DATA.authenticity.certified &&
              <div className="px-4 py-2 bg-amber-50 text-amber-700 rounded-full text-sm font-medium border border-amber-200">
                  ✓ Certified
                </div>
              }
            </div>

            {/* Certification Details */}
            {SAMPLE_DATA.authenticity.certified &&
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Certification Details
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Certified By:</span>
                    <span className="font-medium text-gray-900">{SAMPLE_DATA.authenticity.certifiedBy}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Certificate #:</span>
                    <span className="font-mono text-gray-900">{SAMPLE_DATA.authenticity.certificateNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="text-gray-900">{SAMPLE_DATA.authenticity.certificationDate}</span>
                  </div>
                </div>
              </div>
            }

            {/* About */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">About This Piece</h2>
              <p className="text-gray-700 leading-relaxed">
                {SAMPLE_DATA.description}
              </p>
            </div>

            {/* Signatures */}
            {SAMPLE_DATA.signedBy.length > 0 &&
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
                <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-3">
                  Signed By
                </div>
                <div className="space-y-2">
                  {SAMPLE_DATA.signedBy.map((name, idx) =>
                <div key={idx} className="text-2xl font-light text-gray-900">
                      {name}
                    </div>
                )}
                </div>
              </div>
            }

            {/* Collector Notes */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Collector's Notes</h2>
              <p className="text-gray-600 leading-relaxed italic">
                {SAMPLE_DATA.collectorNotes}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>);

}